import React, { useEffect, useState } from "react"
import DashboardLayout from "@pma/app/dashboard/layout"
import { PROJECT_BY_ID_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Heading } from "@components/common/heading"
import { Card } from "@components/common/card"
import ProjectInfoCard from "@components/projects/project-info-card"
import { ProjectTabs } from "@components/projects/project-tabs"
import ProjectDetailMenu from "@components/projects/project-detail-menu"
import useTranslator from "@hooks/use-translator"
import { toast } from "@hooks/use-toast"
import { Progress } from "@components/common/progress"
import ProjectToolbar from "@components/projects/project-toolbar"
import GisMap from "@components/gis/map"
import { DictType } from "@pma/types"
import { useCustomColumns } from "@pma/stores/tasks-store"
import UnauthorizedCard from "@pma/components/layout/unauthorized-card"
import { UserRoleProvider } from "@pma/contexts/UserRoleContext"
import { usePageContext } from "@pma/stores/page-context-store"

const ProjectDetailPage = () => {
  const [progress, setProgress] = useState(13)
  const [projectOverviewExpand, setProjectOverviewExpand] = useState(false)

  const { translate } = useTranslator()

  const params = useParams()

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const { setCustomColumns } = useCustomColumns()
  const { setPageTitle, clearPageTitle, setBreadcrumbOverride, clearBreadcrumbOverride } = usePageContext()

  const projectId = params?.projectId
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const queryKey = searchParam.get("revalidate")
  const paramLng = searchParam.get("lng")
  const paramLat = searchParam.get("lat")

  const getProject = async () => {
    const response = await axios.get(SERVER_URL + PROJECT_BY_ID_ENDPOINT + `/${projectId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
    return response.data
  }

  const {
    data: project,
    isLoading,
    isFetched,
    refetch,
    isError,
    error,
  } = useQuery({
    queryKey: ["get-project", projectId],
    queryFn: getProject,
    enabled: !!projectId,
  })

  const errorResponse = error as DictType
  const errorStatusCode = errorResponse?.request?.status

  const isUnauthorized = errorStatusCode >= 400 && errorStatusCode < 500
  const serverSideError = errorStatusCode && errorStatusCode >= 500

  useEffect(() => {
    // Revalidate project when projects are updated
    if (queryKey === "projects") {
      refetch()
      return navigate(`${location.pathname}`)
    }

    if (paramLat || paramLng) {
      setProjectOverviewExpand(true)
    }
  }, [queryKey, paramLat, paramLng])

  useEffect(() => {
    // For the progress bar
    const timer1 = setTimeout(() => setProgress(25), 300)
    const timer2 = setTimeout(() => setProgress(50), 500)
    const timer3 = setTimeout(() => setProgress(100), 800)
    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
    }
  }, [])

  useEffect(() => {
    if (project && project?._custom_columns) {
      setCustomColumns(project?._custom_columns)
    }
  }, [project?.id])

  useEffect(() => {
    if (project?.title) {
      setPageTitle(project.title)
      if (projectId) {
        setBreadcrumbOverride(projectId, project.title)
      }
    }
    return () => {
      clearPageTitle()
      if (projectId) {
        clearBreadcrumbOverride(projectId)
      }
    }
  }, [project?.title, projectId])

  useEffect(() => {
    if (serverSideError) {
      toast({
        title: translate("Error"),
        description: translate(errorResponse?.response.data.message),
        variant: "error",
      })
    }
  }, [serverSideError])

  if (isUnauthorized) {
    return (
      <DashboardLayout>
        <UnauthorizedCard status={errorResponse?.response?.status} />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <UserRoleProvider>
        <div className='relative flex w-full flex-col overflow-hidden'>
          <div className='flex items-center justify-between pb-2'>
            {isLoading ? (
              <Progress
                value={progress}
                className='h-2 w-[40%] transition-all duration-300 ease-in-out'
              />
            ) : (
              <Heading title={project?.title} description={project?.description} />
            )}
            {project?.id && <ProjectDetailMenu project={project} />}{" "}
            {serverSideError && (
              <UnauthorizedCard
                status={errorResponse?.response?.status}
                message={errorResponse?.response.data.message}
                sm
              />
            )}
          </div>
          {!serverSideError && (
            <div
              className={`mt-2 flex flex-col justify-between gap-6 md:flex-row ${projectOverviewExpand ? "md:h-auto" : "md:h-auto"} `}
            >
              {projectOverviewExpand && project?.address_text && (
                <Card className='w-full overflow-hidden border-none md:w-1/2'>
                  <GisMap
                    project={project}
                    longitude={project?.address_point?.data?.lng}
                    latitude={project?.address_point?.data?.lat}
                    zoom={17}
                    refetch={refetch}
                  />
                </Card>
              )}

              <ProjectInfoCard
                project={project}
                refetch={refetch}
                sm={projectOverviewExpand ? !!project?.address_text: false}
                isExpanded={projectOverviewExpand}
                setExpanded={setProjectOverviewExpand}
              />
            </div>
          )}

          {/* Project Tabs */}
          <div className='my-4 grid gap-6'>
            {isFetched && <ProjectTabs project={project} revalidate={refetch} />}
          </div>
          <ProjectToolbar project={project} />
        </div>
      </UserRoleProvider>
    </DashboardLayout>
  )
}

export default ProjectDetailPage
